import { CartLinesUpdateEvent } from '@shopify/events';
import { eligibleAdds, hasPaidGiftChoice } from '@theme/oracura-flosser-gift';
import { beginGiftNotice } from '@theme/oracura-flosser-gift-notice';

let giftAdds = Promise.resolve();
export function addItems(items, source = document, options = {}) {
  const eligibleGift = !source?.combo && eligibleAdds(items, source?.flossGiftConfig).length > 0;
  if (!eligibleGift) return postItems(items, source, options, false);
  const next = giftAdds.then(() => postItems(items, source, options, true));
  giftAdds = next.catch(() => {});
  return next;
}

async function postItems(items, source, { checkout = false }, eligibleGift) {
  const base = window.Shopify?.routes?.root || '/';
  if (eligibleGift && typeof window.OracuraBundleGuard?.prepareFlosserAdd !== 'function') {
    throw new Error('Your free MB100 offer is loading. Please try again in a moment.');
  }
  let giftNotice = null;
  if (eligibleGift) {
    const cart = await window.OracuraBundleGuard.readRawCart();
    if (!hasPaidGiftChoice(cart)) giftNotice = beginGiftNotice();
  }
  let giftOffer = null, submitted = items;
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId))].slice(0, 5);
  let response, result;
  try {
    giftOffer = eligibleGift ? await window.OracuraBundleGuard.prepareFlosserAdd(items, source.flossGiftConfig) : null;
    if (eligibleGift && !giftOffer) throw new Error('Your free MB100 could not be prepared. Please try again.');
    submitted = giftOffer?.items || items;
    response = await fetch(`${base}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: submitted, sections: sectionIds, sections_url: location.pathname }),
    });
    // A refused request does not always answer in JSON: a rate limit, a bot
    // check or a server error comes back as an HTML page, and parsing that threw
    // "Unexpected token '<'", which then reached the shopper as the message.
    result = await response.json().catch(() => ({ status: response.status }));
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || window.OracuraLabels?.cartError || 'Unable to add this item. Please try again.');
    }
    if (giftOffer) {
      const verifiedCart = await giftOffer.verify();
      window.dispatchEvent(new CustomEvent('oracura:gift-cart-verified', {detail:verifiedCart}));
      // Warm the SDK's real cart while the card is on screen; opening is gated.
      try { await window.refreshSideCart?.(false, false, true); } catch { /* Retry when opening. */ }
    }
    if (giftNotice) await giftNotice.complete();
  } catch (error) {
    giftNotice?.cancel();
    if (giftOffer) {
      try {
        await giftOffer.rollback();
        if (typeof window.refreshSideCart === 'function') await window.refreshSideCart(false, false, true);
      } catch { throw new Error('Your cart could not be restored. Please check it before continuing.'); }
    }
    throw error;
  }
  if (checkout) {
    // Buy Now. When GoKwik owns checkout it has to own this path too — going to
    // Shopify's own checkout here would skip GoKwik's COD rules, offers and RTO
    // checks and take the order on terms the store does not intend. Their SDK
    // reads the cart itself, so adding the item and opening their flow is enough.
    if (window.OracuraGoKwik?.enabled) {
      // The bridge hands a button to their onCheckoutClick and, given none,
      // looks for the cart drawer's. This store no longer renders that drawer,
      // so there was nothing to find and Buy Now never reached them at all.
      const pressed = source?.querySelector?.('[data-buy-now]') || document.querySelector('[data-buy-now]') || undefined;
      if (window.OracuraGoKwik.checkout(pressed)) return result;
      // Their script is present but did not take the click. Open their cart
      // where the shopper is standing — the checkout button in it retries —
      // rather than sending them to /cart, a page this store bounces out of,
      // or to Shopify's own checkout, which would bypass GoKwik's terms.
      if (typeof window.openGokwikSideCart === 'function') {
        window.openGokwikSideCart();
        return result;
      }
      location.assign(`${base}cart`);
      return result;
    }
    location.assign(`${base}checkout`);
    return result;
  }
  const refresh = fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } })
    .then((res) => { if (!res.ok) throw new Error('Cart refresh failed'); return res.json(); })
    .then((cart) => {
      document.querySelectorAll('[data-plp-cart-count]').forEach((counter) => {
        counter.textContent = cart.item_count;
        counter.hidden = cart.item_count === 0;
      });
      return {
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: result.sections, source: 'oracura-cart', didError: false },
      };
    })
    // The item is in the cart either way; only the count refresh failed. Say
    // so to whoever listens rather than sending the shopper to /cart, a page
    // this store bounces straight back out of.
    .catch(() => ({ detail: { didError: true } }));
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product',
    lines: submitted.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    promise: refresh,
  }));
  await refresh;
  if (giftNotice) {
    try { await window.refreshSideCart?.(false, false, true); } catch { /* The SDK also refreshes on open. */ }
    if (typeof window.openGokwikSideCart === 'function') window.openGokwikSideCart();
    else document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
  }
  return result;
}

window.OracuraCart = { addItems };
